function formatCurrency(value, currency = "DKK") {
  return new Intl.NumberFormat(currency === "DKK" ? "da-DK" : "sv-SE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatQuantity(value) {
  if (value === null || value === undefined || value === "") return "—";

  const num = Number(value);
  if (Number.isFinite(num)) {
    return Number.isInteger(num)
      ? String(num)
      : String(num.toFixed(2)).replace(/\.00$/, "");
  }

  return value;
}

function getRowLabel(row) {
  return row?.label || row?.name || "Untitled row";
}

function getRowSubLabel(row) {
  const sourceLabel = row?.meta?.sourceLabel;
  if (!sourceLabel) return "";

  return String(sourceLabel)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isRowActive(row, fallbackActive = true) {
  if (
    row?.inactive === true ||
    row?.excluded === true ||
    row?.flags?.inactive === true ||
    row?.flags?.excluded === true ||
    row?.meta?.inactive === true ||
    row?.meta?.excluded === true
  ) {
    return false;
  }

  return fallbackActive;
}

function isRowReviewRequired(row) {
  return (
    row?.reviewRequired === true ||
    row?.flags?.reviewRequired === true ||
    row?.flags?.invalidPrice === true ||
    row?.meta?.review === true ||
    row?.materialLibraryMatch === null ||
    row?.labourRuleMatch === null ||
    Number(row?.unitPrice ?? row?.price ?? 1) <= 0 ||
    Number(row?.total ?? 1) <= 0
  );
}

function isPricePending(row) {
  return (
    Number(row?.unitPrice ?? row?.price ?? 1) <= 0 ||
    Number(row?.total ?? 1) <= 0
  );
}

function normalizeGroups(section = {}) {
  if (Array.isArray(section.groups) && section.groups.length > 0) {
    return section.groups;
  }

  if (Array.isArray(section.rows) && section.rows.length > 0) {
    return [
      {
        id: `${section.id || section.key || section.title}-rows`,
        label: section.title || "Rows",
        rows: section.rows,
        rowCount: section.rows.length,
        total: section.rows.reduce(
          (sum, row) => sum + Number(row?.total || 0),
          0
        ),
      },
    ];
  }

  return [];
}

export default function EstimateSection({
  section,
  variant,
  onToggleRowIncluded,
}) {
  const resolvedVariant = variant || section?.variant;
  const fallbackActive = resolvedVariant !== "optional";

  const groups = normalizeGroups(section);
  const sectionRows = groups.flatMap((group) => group.rows || []);

  const activeRows = sectionRows.filter((row) =>
    isRowActive(row, fallbackActive)
  );

  const inactiveRows = sectionRows.filter(
    (row) => !isRowActive(row, fallbackActive)
  );

  const sectionTotal =
    section?.total ??
    activeRows.reduce((sum, row) => sum + Number(row?.total || 0), 0);

  const currency = sectionRows.find((row) => row?.currency)?.currency || "DKK";

  return (
    <section className="bg-white">
      <div className="flex items-center justify-between gap-4 border-b border-[#edebe9] bg-[#faf9f8] px-4 py-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-[#201f1e]">
              {section?.title || "Untitled section"}
            </h3>

            {inactiveRows.length > 0 && (
              <span className="rounded-sm bg-white px-1.5 py-0.5 text-[10px] font-medium uppercase text-[#605e5c]">
                {inactiveRows.length} inactive
              </span>
            )}
          </div>
        </div>

        <div className="shrink-0 text-sm font-semibold tabular-nums text-[#201f1e]">
          {formatCurrency(sectionTotal, currency)}
        </div>
      </div>

      {groups.map((group) => (
        <div
          key={group.id || group.label}
          className="border-b border-[#edebe9] last:border-b-0"
        >
          {groups.length > 1 && (
            <div className="flex items-center justify-between gap-3 border-b border-[#edebe9] bg-white px-4 py-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#605e5c]">
                {group.label}
              </div>

              <div className="text-xs tabular-nums text-[#8a8886]">
                {group.rowCount || group.rows?.length || 0} ·{" "}
                {formatCurrency(group.total, currency)}
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-[#f3f2f1]">
                <tr className="border-b border-[#edebe9]">
                  <HeaderCell />
                  <HeaderCell>Item</HeaderCell>
                  <HeaderCell align="right">Qty</HeaderCell>
                  <HeaderCell align="right">Total</HeaderCell>
                  <HeaderCell>Status</HeaderCell>
                </tr>
              </thead>

              <tbody>
                {(group.rows || []).map((row, index) => {
                  const isActive = isRowActive(row, fallbackActive);
                  const needsReview = isRowReviewRequired(row);
                  const pricePending = isPricePending(row);

                  return (
                    <tr
                      key={row.id || `${group.id}-${index}`}
                      onClick={() => onToggleRowIncluded?.(row)}
                      className={[
                        "group cursor-pointer border-b border-[#edebe9] last:border-b-0",
                        !isActive
                          ? "bg-[#faf9f8] text-[#605e5c] hover:bg-[#f3f2f1]"
                          : needsReview
                          ? "bg-[#fff4ce] hover:bg-[#fff0b3]"
                          : "bg-white hover:bg-[#f3f2f1]",
                      ].join(" ")}
                    >
                      <td className="w-12 px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={(event) => {
                            event.stopPropagation();
                            onToggleRowIncluded?.(row);
                          }}
                          onClick={(event) => event.stopPropagation()}
                          className="h-4 w-4 border-[#605e5c]"
                        />
                      </td>

                      <td className="min-w-[260px] px-3 py-2 align-top">
                        <div
                          className={[
                            "font-medium",
                            isActive ? "text-[#201f1e]" : "text-[#605e5c]",
                          ].join(" ")}
                        >
                          {getRowLabel(row)}
                        </div>

                        {getRowSubLabel(row) && (
                          <div className="mt-0.5 text-xs text-[#8a8886]">
                            {getRowSubLabel(row)}
                          </div>
                        )}
                      </td>

                      <td className="w-24 px-3 py-2 text-right align-top tabular-nums text-[#605e5c]">
                        {formatQuantity(row?.quantity ?? row?.qty ?? row?.hours)}
                      </td>

                      <td className="w-32 px-3 py-2 text-right align-top font-medium tabular-nums">
                        {pricePending ? (
                          <span className="text-[#8a6d1d]">Pending</span>
                        ) : (
                          <span
                            className={
                              isActive ? "text-[#201f1e]" : "text-[#605e5c]"
                            }
                          >
                            {formatCurrency(row?.total, row?.currency || currency)}
                          </span>
                        )}
                      </td>

                      <td className="w-28 px-3 py-2 align-top">
                        {needsReview && isActive ? (
                          <Status tone="warning">Review</Status>
                        ) : isActive ? (
                          <Status>Active</Status>
                        ) : (
                          <Status muted>Inactive</Status>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </section>
  );
}

function HeaderCell({ children, align = "left" }) {
  return (
    <th
      className={[
        "px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#605e5c]",
        align === "right" ? "text-right" : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}

function Status({ children, tone, muted = false }) {
  if (tone === "warning") {
    return (
      <span className="rounded-sm bg-[#fff4ce] px-1.5 py-0.5 text-[10px] font-medium uppercase text-[#8a6d1d]">
        {children}
      </span>
    );
  }

  return (
    <span
      className={[
        "text-xs",
        muted ? "text-[#8a8886]" : "text-[#605e5c]",
      ].join(" ")}
    >
      {children}
    </span>
  );
}